import { useAccount } from "wagmi";
import once from "lodash/once";
import { useCallback, useContext, useEffect, useState } from "react";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { CSRFContext } from "gui/csrf/csrf";
import { useSignTypedData } from "wagmi";
import { SessionContext } from "gui/session/session";
import { getSigningMessage } from "./getSigningMessage";
import { disconnect } from "@wagmi/core";

export function useSignMessage(
  onSign: (signature: string, address: string) => Promise<void>
): { signMessage: () => void; error: Error | null; loading: boolean } {
  const session = useContext(SessionContext);
  const { csrf } = useContext(CSRFContext);
  const [error, setError] = useState<Error | null>(null);

  const { openConnectModal, connectModalOpen } = useConnectModal();
  useOnModelEarlyExit(() => {
    setSignMessage(false);
  });

  const { address } = useAccount();

  const [signMessage, setSignMessage] = useState(false);

  const {
    data: signature,
    signTypedData,
    reset: resetSignature,
  } = useSignTypedData({
    onError: (err) => {
      setSignMessage(false);
      resetSignature();
      setError(err);
      disconnect();
    },
  });

  const openModal = useCallback(() => {
    if (address === undefined && !connectModalOpen) {
      if (openConnectModal === undefined) {
        throw new Error("openConnectModel should not be undefined");
      }
      openConnectModal();
    }
  }, [openConnectModal, address, connectModalOpen]);

  const getSignature = once(() => {
    return getSigningMessage(String(address), csrf)
      .then((data) => {
        signTypedData(data);
      })
      .catch((err) => {
        setError(err);
        resetSignature();
        setSignMessage(false);
        disconnect();
      });
  });

  const handleSignedMessage = (sig: string) => {
    onSign(sig, String(address))
      .then(() => {
        session.refresh();
      })
      .catch((err) => {
        setError(err);
      })
      .finally(() => {
        resetSignature();
        setSignMessage(false);
        disconnect();
      });
  };

  useEffect(() => {
    if (signMessage) {
      if (address === undefined && !connectModalOpen) {
        openModal();
        return;
      }
      if (signature === undefined) {
        getSignature();
        return;
      }

      if (signature !== undefined) {
        handleSignedMessage(signature);
      }
    }
  }, [signMessage, address, signature]);

  return {
    signMessage: () => {
      setSignMessage(true);
    },
    error,
    loading: signMessage,
  };
}

const useOnModelEarlyExit = (onEarlyExit: () => void) => {
  const { connectModalOpen } = useConnectModal();
  const [connectModalWasOpen, setConnectModalWasOpen] = useState(
    connectModalOpen
  );
  const { address } = useAccount();
  useEffect(() => {
    if (connectModalWasOpen && !connectModalOpen && address === undefined) {
      onEarlyExit();
    }
    setConnectModalWasOpen(connectModalOpen);
  }, [connectModalOpen, address]);
};
